import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send, Plus, AlertCircle } from "lucide-react";

const VENUES = ["Main Sanctuary", "Chapel", "STREAM Center", "Classroom"];
const MEDIA_TYPES = ["Sound", "Visual/Projection", "Music (Playback)", "Zoom"];
const INSTRUMENTS = ["Keyboard", "Drums", "Guitar amp"];
const PLAYBACK = ["MP3/Aux"];
const VISUAL_OPTIONS = [
  "Photography",
  "Videography",
  "Social Media Advertisements",
  "Social Media Streaming",
];

const EMPTY = {
  email: "",
  contact_name: "",
  phone: "",
  event_dates: "",
  event_start_time: "",
  event_end_time: "",
  event_description: "",
  venue: "",
  media_types: [],
  audio_wireless_handheld_qty: "",
  audio_wired_mic_qty: "",
  instruments: [],
  playback: [],
  visual_options: [],
  additional_request: "",
};

export default function MinistryMedia() {
  const { user } = useAuth() || {};
  const [form, setForm] = useState({
    ...EMPTY,
    email: user?.email || "",
    contact_name: user?.name || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api.get("/media-requests/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const u = (k) => (e) => set(k, e.target.value);
  const toggleArr = (k, value) => {
    setForm((f) => {
      const arr = f[k] || [];
      return { ...f, [k]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.venue) {
      toast.error("Please choose a venue.");
      return;
    }
    if (form.media_types.length === 0) {
      toast.error("Please choose at least one Type of Media.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        audio_wireless_handheld_qty: form.audio_wireless_handheld_qty
          ? parseInt(form.audio_wireless_handheld_qty, 10)
          : null,
        audio_wired_mic_qty: form.audio_wired_mic_qty
          ? parseInt(form.audio_wired_mic_qty, 10)
          : null,
      };
      // Strip empty optionals
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      await api.post("/media-requests", payload);
      toast.success("Media request submitted! LaTonya Phillips will be in touch.");
      setShowForm(false);
      setForm({
        ...EMPTY,
        email: form.email,
        contact_name: form.contact_name,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="ministry-media-page">
      <PageHero
        overline="SMZBT Media Request"
        title={<>Request <em className="text-gold-light italic font-light">media</em> support.</>}
        kicker="Sound, projection, music playback, Zoom, photography, and videography for your ministry event."
      />

      <Container className="pb-32">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Overline>Your Requests</Overline>
            <h2 className="font-serif text-3xl md:text-4xl font-black">My Media Requests</h2>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-wine px-6 py-3 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2"
            data-testid="new-request-btn"
          >
            <Plus className="h-4 w-4" /> {showForm ? "Close" : "New Request"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="glass p-10 mb-12 space-y-10" data-testid="media-request-form">
            {/* Lead-time notice */}
            <div className="flex items-start gap-3 bg-gold/[0.08] border-l-2 border-gold p-5">
              <AlertCircle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-white/85 leading-relaxed">
                Requests should be submitted <strong>at least 3 weeks prior</strong> to the event.
                Form is routed to <strong>LaTonya Phillips</strong>. All requests are subject to final approval.
              </p>
            </div>

            {/* CONTACT */}
            <Section heading="Contact">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                <Field label="Email" required>
                  <input data-testid="mr-email" type="email" required value={form.email} onChange={u("email")} className={inputCls} />
                </Field>
                <Field label="Contact (First & Last Name)" required>
                  <input data-testid="mr-contact-name" required value={form.contact_name} onChange={u("contact_name")} className={inputCls} />
                </Field>
                <Field label="Phone Number" required className="md:col-span-2">
                  <input data-testid="mr-phone" required value={form.phone} onChange={u("phone")} className={inputCls} placeholder="(757) 555-0100" />
                </Field>
              </div>
            </Section>

            {/* EVENT */}
            <Section heading="Event">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                <Field label="Date(s) of your event" required className="md:col-span-2" hint="Include full dates with days of the week — e.g. Sat 6/14, Sun 6/15">
                  <input data-testid="mr-event-dates" required value={form.event_dates} onChange={u("event_dates")} className={inputCls} />
                </Field>
                <Field label="Event Start Time" required>
                  <input data-testid="mr-event-start" type="time" required value={form.event_start_time} onChange={u("event_start_time")} className={inputCls} />
                </Field>
                <Field label="Event End Time" required>
                  <input data-testid="mr-event-end" type="time" required value={form.event_end_time} onChange={u("event_end_time")} className={inputCls} />
                </Field>
                <Field label="Event Description" className="md:col-span-2">
                  <textarea data-testid="mr-event-desc" rows={3} value={form.event_description} onChange={u("event_description")} className={inputCls} />
                </Field>
              </div>

              <Field label="Venue" required className="mt-8">
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="mr-venue">
                  {VENUES.map((v) => (
                    <RadioCard
                      key={v}
                      checked={form.venue === v}
                      onClick={() => set("venue", v)}
                      label={v}
                      testId={`mr-venue-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </div>
              </Field>
            </Section>

            {/* MEDIA TYPE */}
            <Section heading="Type of Media Required">
              <Field label="Select all that apply" required>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="mr-media-types">
                  {MEDIA_TYPES.map((m) => (
                    <CheckCard
                      key={m}
                      checked={form.media_types.includes(m)}
                      onClick={() => toggleArr("media_types", m)}
                      label={m}
                      testId={`mr-media-${m.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    />
                  ))}
                </div>
              </Field>
            </Section>

            {/* AUDIO QUANTITIES */}
            <Section heading="Audio">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                <Field label="Wireless Handheld Mics" hint="Quantity needed">
                  <QtySelect
                    value={form.audio_wireless_handheld_qty}
                    onChange={(v) => set("audio_wireless_handheld_qty", v)}
                    max={6}
                    testId="mr-wireless-qty"
                  />
                </Field>
                <Field label="Wired Mics" hint="Quantity needed">
                  <QtySelect
                    value={form.audio_wired_mic_qty}
                    onChange={(v) => set("audio_wired_mic_qty", v)}
                    max={4}
                    testId="mr-wired-qty"
                  />
                </Field>
              </div>
            </Section>

            {/* INSTRUMENTS + PLAYBACK */}
            <Section heading="Instruments & Playback">
              <Field label="Instruments needed">
                <div className="grid sm:grid-cols-3 gap-3" data-testid="mr-instruments">
                  {INSTRUMENTS.map((i) => (
                    <CheckCard
                      key={i}
                      checked={form.instruments.includes(i)}
                      onClick={() => toggleArr("instruments", i)}
                      label={i}
                      testId={`mr-instrument-${i.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </div>
              </Field>
              <Field label="Playback" className="mt-6">
                <div className="grid sm:grid-cols-3 gap-3" data-testid="mr-playback">
                  {PLAYBACK.map((p) => (
                    <CheckCard
                      key={p}
                      checked={form.playback.includes(p)}
                      onClick={() => toggleArr("playback", p)}
                      label={p}
                      testId={`mr-playback-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    />
                  ))}
                </div>
              </Field>
            </Section>

            {/* VISUAL */}
            <Section heading="Visual">
              <Field label="Visual services needed">
                <div className="grid sm:grid-cols-2 gap-3" data-testid="mr-visual">
                  {VISUAL_OPTIONS.map((v) => (
                    <CheckCard
                      key={v}
                      checked={form.visual_options.includes(v)}
                      onClick={() => toggleArr("visual_options", v)}
                      label={v}
                      testId={`mr-visual-${v.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    />
                  ))}
                </div>
              </Field>
            </Section>

            {/* ADDITIONAL */}
            <Section heading="Additional">
              <Field label="Additional Request">
                <textarea
                  data-testid="mr-additional"
                  rows={4}
                  value={form.additional_request}
                  onChange={u("additional_request")}
                  className={inputCls}
                  placeholder="Anything else our media team should know? Specific cues, special needs, song files, etc."
                />
              </Field>
            </Section>

            <div className="flex justify-end pt-4 border-t border-white/10">
              <button
                type="submit"
                disabled={submitting}
                className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="mr-submit"
              >
                <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        )}

        {mine.length === 0 ? (
          <div className="glass p-12 text-center">
            <p className="font-serif italic text-white/60 text-lg">You haven't submitted any requests yet.</p>
          </div>
        ) : (
          <div className="space-y-px bg-white/5">
            {mine.map((r) => (
              <div key={r.id} className="bg-wine-950 p-8 grid md:grid-cols-12 gap-6 items-start" data-testid={`my-request-${r.id}`}>
                <div className="md:col-span-2 text-xs uppercase tracking-widest text-gold font-bold">
                  {r.venue || r.request_type || "Media"}
                </div>
                <div className="md:col-span-7">
                  <h4 className="font-serif text-xl font-bold">
                    {r.event_dates || r.title || "Media Request"}
                  </h4>
                  <p className="text-sm text-white/60 mt-1">
                    {r.event_start_time && r.event_end_time
                      ? `${r.event_start_time} – ${r.event_end_time}`
                      : r.needed_by ? `Need by: ${r.needed_by}` : ""}
                  </p>
                  {(r.media_types || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {r.media_types.map((m) => (
                        <span key={m} className="text-[10px] uppercase tracking-wider bg-wine-700/40 px-2.5 py-1 text-white/75">{m}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 text-sm text-white/60">
                  {r.contact_name && <div>{r.contact_name}</div>}
                  {r.phone && <div className="text-white/45">{r.phone}</div>}
                </div>
                <div className="md:col-span-1 text-right">
                  <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-widest rounded-full font-bold ${
                    r.status === "pending" ? "bg-gold/20 text-gold" :
                    r.status === "completed" ? "bg-green-500/20 text-green-300" :
                    "bg-white/10 text-white/70"
                  }`}>{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}

/* ---------- Helpers ---------- */
const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";

function Section({ heading, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-5 pb-3 border-b border-white/10">
        {heading}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children, className = "", hint }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">
        {label}{required && <span className="text-gold ml-1">*</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-white/45 mt-2">{hint}</p>}
    </label>
  );
}

function RadioCard({ checked, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-4 py-3 border transition-colors text-sm font-bold tracking-tight ${
        checked
          ? "border-gold bg-gold/10 text-gold"
          : "border-white/15 hover:border-white/40 text-white/80"
      }`}
    >
      {label}
    </button>
  );
}

function CheckCard({ checked, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-4 py-3 border transition-colors text-sm font-bold tracking-tight flex items-center gap-3 ${
        checked
          ? "border-gold bg-gold/10 text-gold"
          : "border-white/15 hover:border-white/40 text-white/80"
      }`}
    >
      <span
        className={`shrink-0 w-4 h-4 border ${
          checked ? "bg-gold border-gold" : "border-white/40"
        } flex items-center justify-center`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="w-3 h-3 text-wine-950" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M3 8l3 3 7-7" />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

function QtySelect({ value, onChange, max, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    >
      <option value="" className="bg-wine-950">— None —</option>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n} className="bg-wine-950">{n}</option>
      ))}
    </select>
  );
}
